import React from 'react'
import { CacheProvider } from '@emotion/react'
import './src/fonts/fonts.css'
import '@fontsource-variable/jetbrains-mono'
import '@fontsource/permanent-marker/400.css'
import '@fontsource/roboto-mono/400.css'
import '@fontsource/roboto-mono/500.css'
import '@fontsource/roboto-mono/700.css'

import { getEmotionCache } from './src/emotionCache'
import { ThemeProvider } from './src/theme/Provider'
import { ReactQueryProvider } from './src/providers/ReactQuery'
import { CartProvider } from './src/providers/Cart'
import { MarketProvider } from './src/providers/Market'
import { CartDrawer } from './src/components/CartDrawer'
import { CheckoutDialog } from './src/components/CheckoutDialog'
import { captureSearchParams } from './src/utils/appUrl'

// wrapRootElement persists across client navigation AND is re-run by Gatsby's
// Head API into a hidden root. Keep it to style-free context providers only:
// any MUI/emotion here renders into the Head root, gets tagged data-gatsby-head,
// and is reaped as "stale" on Head re-render — stranding emotion's tracked
// <style> tags and crashing the next insert. See wrapPageElement for the UI.
export const wrapRootElement = ({ element }) => (
  <ReactQueryProvider>
    <MarketProvider>
      <CartProvider>{element}</CartProvider>
    </MarketProvider>
  </ReactQueryProvider>
)

// Page-only: never touched by the Head reconciler. All emotion/MUI lives here.
export const wrapPageElement = ({ element }) => (
  <CacheProvider value={getEmotionCache()}>
    <ThemeProvider>
      {element}
      <CartDrawer />
      <CheckoutDialog />
    </ThemeProvider>
  </CacheProvider>
)

// Force every cross-page CTA without an explicit section anchor to land at
// the top of the destination page. Gatsby's default scroll restoration can
// land mid-page when image/font loading shifts layout after navigation —
// this forces (0, 0) for hashless paths and defers to the hash for anchored
// links (e.g. /pay#flow, /jobs#top). Belt-and-suspenders: also imperatively
// scroll to top after Gatsby's own restoration runs.
// Persist query params on first load and every route change so attribution
// survives internal <Link> navigation (which drops the query string) all the
// way to the biz.qwick.com hand-off. See src/utils/appUrl.ts.
export const onRouteUpdate = ({ location }) => {
  captureSearchParams(location.search)
}

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location.hash) return true
  if (typeof window !== 'undefined') {
    window.requestAnimationFrame(() => window.scrollTo(0, 0))
  }
  return [0, 0]
}
